// Detects "structural" queries that should navigate directly rather than
// hitting the full-text index. Returns a string URL or null.
//
// Examples:
//   "Matthew 5:8"      → /book/matthew/chapter/5#v=8
//   "Matt 5"           → /book/matthew/chapter/5
//   "John 3:16"        → /book/john/chapter/3#v=16
//   "Ver. 18 John 3"   → /book/john/chapter/3#v=18

import { NEW_TESTAMENT, OLD_TESTAMENT, slugify } from "../data/books";

const stripPrefix = (n) => n.replace(/^st\.?\s+/i, "");

const ALL_BOOKS = [...NEW_TESTAMENT, ...OLD_TESTAMENT].map((b) => ({
  ...b,
  slug: slugify(stripPrefix(b.name)),
  // Lower-cased lookup helpers
  lc: b.name.toLowerCase(),
  lcStripped: stripPrefix(b.name).toLowerCase(),
}));

function findBook(token) {
  const t = token.toLowerCase();
  // Exact match on stripped lowercase first
  return (
    ALL_BOOKS.find((b) => b.lcStripped === t) ||
    ALL_BOOKS.find((b) => b.lc === t) ||
    ALL_BOOKS.find((b) => b.lcStripped.startsWith(t)) ||
    ALL_BOOKS.find((b) => b.lc.includes(t)) ||
    null
  );
}

export function parseStructural(raw) {
  const q = String(raw || "").trim();
  if (!q) return null;

  // Pattern: "Ver. N <book ref>"
  const m1 = q.match(/^ver\.?\s*(\d{1,3})\s+(?:of\s+)?(.+)$/i);
  if (m1) {
    const verse = parseInt(m1[1], 10);
    const sub = parseStructural(m1[2]);
    if (sub && /\/chapter\/\d+/.test(sub)) {
      return sub.replace(/(#v=\d+)?$/, `#v=${verse}`);
    }
  }

  // Pattern: "<book> <chapter>:<verse>" or "<book> <chapter>" or "<book>"
  const m2 = q.match(
    /^(.+?)\s+(\d{1,3})(?:\s*[:.\s]\s*(\d{1,3}))?\s*$/,
  );
  if (m2) {
    const bookPart = m2[1].trim();
    const chap = parseInt(m2[2], 10);
    const verse = m2[3] ? parseInt(m2[3], 10) : null;
    const book = findBook(bookPart);
    if (book && chap >= 1 && chap <= book.chapters) {
      const slug = book.slug;
      const url = `/book/${slug}/chapter/${chap}`;
      return verse ? `${url}#v=${verse}` : url;
    }
  }

  return null;
}
